import AddIcon from '@mui/icons-material/Add';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import { Button, Drawer, Grid, IconButton, TableCell, TextField, Typography } from '@mui/material';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import MainCard from 'components/MainCard';
import IndexTable from 'components/index-table';
import globalConstants from 'constants/global.constants';
import useDebounce from 'hooks/useDebounce';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFetchCategory, useProductTableData } from 'services/product.service';
import { authSelector } from 'store/auth';
import { useOnlineStatus } from 'utils/useOnlineStatus';
import { productsTableConfig, productsTablePayload } from './config';
import ProductForm from './product-form';

const defaultFormData = {
  formType: globalConstants.FORM_TYPE.CREATE,
  isOpen: false,
  formId: null,
};

const Products = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedValue = useDebounce<string>(searchTerm, 500);
  const isOnline = useOnlineStatus();
  const { permissions } = useSelector(authSelector);

  const [payload, setPayload] = useState(productsTablePayload);
  const { data, isFetching } = useProductTableData(payload, isOnline);
  const { data: categoryList = { results: [] } } = useFetchCategory(isOnline);
  const catMap = useMemo(
    () => Object.assign({}, ...categoryList.results.map((cat: any) => ({ [cat.cat_id]: cat }))),
    [categoryList.results],
  );
  const [formConfig, setFormConfig] = useState(defaultFormData);

  const handleChange = (e: any) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    if (debouncedValue) {
      setPayload({
        ...payload,
        conditions: [
          ...payload.conditions.filter(p => p.field !== 'name'),
          { field: 'name', condition: 'LIKE', value: `%${debouncedValue}%` },
        ],
      });
    } else {
      setPayload({ ...payload, conditions: payload.conditions.filter(p => p.field !== 'name') });
    }
  }, [debouncedValue]);

  const getCustomCell = (column: any, row: any, rowIndex: number, columnIndex: number) => {
    if (column.id === 'sno') {
      return (
        <TableCell className={`th_col_${columnIndex}`} align={column.align} padding={'none'}>
          {(productsTablePayload?.pagination?.skip || 0) + rowIndex + 1}
        </TableCell>
      );
    }
    if (column.id === 'category') {
      return (
        <TableCell className={`th_col_${columnIndex}`} align={column.align} padding={'none'}>
          {catMap[row.cat_id]?.name}
        </TableCell>
      );
    }
    return (
      <TableCell className={`th_col_${columnIndex}`} align={column.align} padding={'none'}>
        {permissions.addProduct && (
          <IconButton aria-label="delete" color="primary" onClick={() => handleOnClick(row)}>
            <SaveAsIcon />
          </IconButton>
        )}
      </TableCell>
    );
  };
  const handleOnClick = (row: any) => {
    setFormConfig({ formType: globalConstants.FORM_TYPE.UPDATE, isOpen: true, formId: row.pid });
  };
  return (
    <>
      <Grid container justifyContent="space-between" alignItems="center" sx={{ pb: 2, gap: 1 }}>
        <MuiBreadcrumbs aria-label="breadcrumb">
          <Typography color="textSecondary" variant="h6" sx={{ textDecoration: 'none' }}>
            Master
          </Typography>
          <Typography variant="subtitle1" color="textPrimary">
            Products
          </Typography>
        </MuiBreadcrumbs>
        <Grid item>
          <Grid item container alignItems="center" gap={1}>
            <TextField
              autoFocus
              id="outlined-basic"
              placeholder="Search"
              value={searchTerm}
              variant="outlined"
              onChange={handleChange}
            />
            {permissions.addProduct && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setFormConfig({ ...defaultFormData, isOpen: true })}
              >
                Add Product
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
      <MainCard>
        <IndexTable
          isFetching={isFetching}
          columns={productsTableConfig}
          data={data}
          payload={payload}
          getCustomCell={getCustomCell}
          onPayloadChange={(newPayload: any) => setPayload(newPayload)}
          clearFilters={() => {
            setSearchTerm('');
            setPayload({ ...payload, conditions: [] });
          }}
          onRowClick={(row: any) => handleOnClick(row)}
        />
      </MainCard>
      <Drawer
        variant="temporary"
        open={formConfig.isOpen}
        ModalProps={{ keepMounted: true }}
        anchor="right"
        onClose={() => setFormConfig(defaultFormData)}
      >
        {formConfig.isOpen && <ProductForm onClose={() => setFormConfig(defaultFormData)} formConfig={formConfig} />}
      </Drawer>
    </>
  );
};

export default Products;
